import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface ListQueryState {
  page: number;
  limit: number;
  tab: string;
  search: string;
  city?: string;
}

interface Options {
  defaultTab?: string;
  defaultLimit?: number;
  allowedLimits?: number[];
}

const DEFAULTS: Required<
  Pick<ListQueryState, "page" | "limit" | "tab" | "search">
> = {
  page: 1,
  limit: 25,
  tab: "all",
  search: "",
};

export const useListQueryState = (opts: Options = {}) => {
  const defaultTab = opts.defaultTab ?? DEFAULTS.tab;
  const defaultLimit = opts.defaultLimit ?? DEFAULTS.limit;
  const allowed = opts.allowedLimits;
  const [params, setParams] = useSearchParams();

  const state = useMemo<ListQueryState>(() => {
    const rawPage = Number(params.get("page"));
    const rawLimit = Number(params.get("limit"));
    const page =
      Number.isFinite(rawPage) && rawPage >= 1
        ? Math.floor(rawPage)
        : DEFAULTS.page;
    let limit =
      Number.isFinite(rawLimit) && rawLimit >= 1
        ? Math.floor(rawLimit)
        : defaultLimit;
    if (allowed && !allowed.includes(limit)) limit = defaultLimit;
    return {
      page,
      limit,
      tab: params.get("tab") ?? defaultTab,
      search: params.get("q") ?? "",
      city: params.get("city") ?? undefined,
    };
  }, [params, defaultTab, defaultLimit, allowed]);

  const patch = useCallback(
    (next: Partial<ListQueryState> & { resetPage?: boolean }) => {
      setParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          const apply = (
            key: string,
            value: string | undefined,
            defaultVal: string | undefined,
          ) => {
            if (value === undefined || value === "" || value === defaultVal)
              sp.delete(key);
            else sp.set(key, value);
          };
          if ("tab" in next) apply("tab", next.tab, defaultTab);
          if ("search" in next) apply("q", next.search, "");
          if ("city" in next) apply("city", next.city, undefined);
          if ("limit" in next && next.limit !== undefined) {
            apply("limit", String(next.limit), String(defaultLimit));
          }
          const pageChanging = "page" in next && next.page !== undefined;
          const nonPageFieldChanged =
            next.resetPage ||
            "tab" in next ||
            "search" in next ||
            "city" in next ||
            "limit" in next;
          if (pageChanging) {
            apply("page", String(next.page), "1");
          } else if (nonPageFieldChanged) {
            sp.delete("page");
          }
          return sp;
        },
        { replace: true },
      );
    },
    [setParams, defaultTab, defaultLimit],
  );

  return { state, patch };
};
